<template>
  <el-form-item class="w-100" :label="label" :prop="prop" label-position="top">
    <el-select
      v-model="select"
      :clearable="clearable"
      :disabled="disabled"
      filterable
      placeholder="Select"
      size="large"
      :loading="loading"
    >
      <el-option
        v-for="item in data"
        :key="item.id"
        :label="getLabel(item)"
        :value="item.id"
      />
    </el-select>
  </el-form-item>
</template>

<script lang="ts" setup>
import { ref, onMounted, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import axios from '@/utils/apiClient'

const { locale } = useI18n()  // reactive locale

const props = defineProps({
  modelValue: {
    type: [Number, null],
    default: null,
  },
  label: {
    type: String,
    default: 'Time Mode',
  },
  prop: {
    type: String,
    default: null,
  },
  clearable: {
    type: Boolean,
    default: false,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  defaultValue: {
    type: [Number, null],
    default: null,
  },
})

interface TimeMode {
  id: number
  mode: string
  modeKhmer: string
  isDefault: boolean
}

const emit = defineEmits(['update:modelValue'])

const data = ref<TimeMode[]>([])
const select = ref<number | null>(props.modelValue)
const loading = ref(false)

const fetch = async () => {
  loading.value = true
  try {
    const res = await axios.get('TimeMode')
    // Only show enabled time modes
    const allData = res.data?.data || []
    data.value = allData.filter((item: any) => item.isEnable === true)

    // Automatically select default item if no modelValue is provided
    if (props.modelValue == null) {
      if (props.defaultValue != null) {
        select.value = props.defaultValue
      } else {
        const defaultItem = data.value.find(item => item.isDefault)
        if (defaultItem) select.value = defaultItem.id
      }
      emit('update:modelValue', select.value)
    }
  } catch (error) {
    console.error('Error fetching data:', error)
  } finally {
    loading.value = false
  }
}

onMounted(fetch)

// Watch for external modelValue changes
watch(
  () => props.modelValue,
  (newValue) => {
    if (newValue !== select.value) select.value = newValue
  }
)

// Emit changes when select changes
watch(select, (newValue) => emit('update:modelValue', newValue))

// Reactive label based on locale
const getLabel = (item: TimeMode) => {
  return locale.value === 'en' ? item.mode : item.modeKhmer
}
</script>
